import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { FaPaperPlane, FaPaperclip, FaTimes } from 'react-icons/fa';
import { NetworkProvider } from '../../NetworkProvider';

const SuggestionPage = ({ selectedPerson }) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [file, setFile] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        
        if (value.trim() === '') {
            setEmailError(t("E-pasta adrese ir obligāta"));
        } else if (!validateEmail(value)) {
            setEmailError(t("Lūdzu, ievadiet derīgu e-pasta adresi"));
        } else {
            setEmailError('');
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];

        if (selectedFile) {
            // Validate file type
            const allowedTypes = [
                'image/jpeg',
                'image/jpg',
                'image/png',
                'image/gif',
                'image/webp',
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ];

            if (!allowedTypes.includes(selectedFile.type)) {
                setError(t("Lūdzu, augšupielādējiet tikai attēlus vai dokumentus (PDF, Word, Excel)"));
                e.target.value = null;
                return;
            }

            // Validate file size (max 10MB)
            if (selectedFile.size > 10 * 1024 * 1024) {
                setError(t("Faila izmērs nedrīkst pārsniegt 10MB"));
                e.target.value = null;
                return;
            }

            setFile(selectedFile);
            setError(null);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate email before submission
        if (!email.trim()) {
            setEmailError(t("Atstāj e-pasta adresi, lai mēs varētu ar tevi sazināties."));
            return;
        }

        if (!validateEmail(email)) {
            setEmailError(t("Lūdzu, ievadiet derīgu e-pasta adresi"));
            return;
        }

        if (!text.trim()) {
            return;
        }

        setLoading(true);

        try {
            let summary = `E-pasts: ${email}\n\n`;

            if (selectedPerson) {
                summary += `${selectedPerson.name} ${selectedPerson.surname}`;
                summary += `\n id=${selectedPerson.id}`
                if (selectedPerson?.table) {
                    summary += `\n tabula=`
                    summary += {
                        1: t("Latviešu strēlnieku 2. brigādes apbalvoto karavīru saraksts"),
                        2: t("Latviešu SS leģionā mobilizēto saraksts"),
                        3: t("Kritušo, bez vēsts pazudušo, ievainoto un gūstā nonākušo latviešu strēlnieku saraksts"),
                        4: t("Zedelgemas (Beļģija) karagūstekņu nometnē ieslodzīto latviešu karavīru saraksts"),
                        5: t("Latviešu strēlnieku bataljonos (pulkos) dienējušo saraksts"),
                        6: t("Kritušo un bez vēsts pazudušo Latviešu SS leģiona karavīru saraksts")
                    }[selectedPerson.table] || t("Nav minēts");
                } else {
                    summary += t("Nav minēts");
                }
            }

            summary += `\n\n${text}`

            await NetworkProvider.suggestion(summary, file);

            setSubmitted(true);
            setText('');
            setEmail('');
            setEmailError('');
            setFile(null);
            handleRemoveFile();
            setTimeout(() => setSubmitted(false), 3000);
        } catch (error) {
            setError(t("Neizdevās nosūtīt"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="mt-5" style={{ maxWidth: '600px' }}>
            {selectedPerson == null && <h2 className="mb-4">{t("Ieteikt labojumu")}</h2>}

            {submitted && (
                <Alert variant="success">
                    {t("Paldies! Jūsu ieteikums ir saņemts")}
                </Alert>
            )}

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-2">
                    <Form.Label className="text-wrap" style={{ wordBreak: 'break-word' }}>
                        {t("Ja pamanāt kļūdas datos, lūdzu, nosūtiet mums savu ieteikumu vai labojumu")}
                    </Form.Label>
                </Form.Group>

                {selectedPerson && (
                    <div className="d-flex flex-wrap align-items-center mb-3">
                        <strong className="me-2">
                            {selectedPerson.name} {selectedPerson.surname}
                        </strong>
                        <span className="text-muted">
                            {selectedPerson.birthdate ?? 'Nav datu'} - {selectedPerson.death_date ?? 'Nav datu'}
                        </span>
                    </div>
                )}

                <div className="d-flex flex-column" style={{ gap: '1rem' }}>
                    {/* Email Field */}
                    <Form.Group>
                        <Form.Label>{t("E-pasta adrese")} *</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder={t("E-pasta adrese")}
                            value={email}
                            onChange={handleEmailChange}
                            disabled={loading}
                            className="rounded-sm"
                            isInvalid={!!emailError}
                        />
                        {emailError && (
                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                {emailError}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <Form.Control
                        as="textarea"
                        rows={5}
                        placeholder={t("Aprakstiet problēmu vai datu neatbilstību")}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="rounded-sm w-100"
                        style={{ resize: 'vertical' }}
                        disabled={loading}
                    />

                    {/* File Upload Section */}
                    <div>
                        <Form.Label
                            htmlFor="file-upload"
                            className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
                            style={{ cursor: 'pointer' }}
                        >
                            <FaPaperclip className="me-2" />
                            {t("Pievienot failu (neobligāti)")}
                        </Form.Label>
                        <small className="text-muted d-block text-center mt-1">
                            {t("Maksimālais faila izmērs")}: 10MB
                        </small>
                        <Form.Control
                            id="file-upload"
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            style={{ display: 'none' }}
                            disabled={loading}
                        />

                        {file && (
                            <div className="mt-2 p-2 border rounded d-flex align-items-center justify-content-between">
                                <span className="text-truncate me-2">
                                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={handleRemoveFile}
                                    disabled={loading}
                                    className="text-danger p-0"
                                >
                                    <FaTimes />
                                </Button>
                            </div>
                        )}
                    </div>

                    <Button
                        variant="primary"
                        type="submit"
                        disabled={!text.trim() || loading}
                        className="rounded-sm w-100 d-flex align-items-center justify-content-center"
                    >
                        {loading ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    className="me-2"
                                />
                                {t("Nosūtīšana...")}
                            </>
                        ) : (
                            <>
                                <FaPaperPlane className="me-2" style={{ paddingBottom: 3 }} />
                                {t("Nosūtīt")}
                            </>
                        )}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default SuggestionPage;